import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Checkbox, InputGroup } from "@blueprintjs/core";
import { HoverCard } from "@mantine/core";

import { AppToaster, ShareLink, TeamsTierUpsell } from "@components";
import { OrgContext } from "@providers";
import { Feature, withToaster } from "@utils";

import { createToken, deleteToken, updateToken } from "@pages/settings/api";
import { Token, TokenResponse } from "@pages/settings/types";

interface Props {
  onFinish: () => void;
  existingToken?: Token;
  orgId: string;
}

const DialogContainer = styled.div`
  padding: 24px;
  padding-bottom: 0px;
  width: 800px;
  background: white;
`;

const Path = styled(ShareLink)`
  background-color: rgb(255, 240, 225);
  margin: 10px;
  font-size: var(--global-font-size-m);
`;

const NewTokenRow = styled.div`
  margin: 20px 0;
`;

const NewTokenContainer = styled.div`
  border: var(--global-purple-highlight) solid 1px;
  border-radius: 5px;
  padding: 10px;
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RoleCheckbox: React.FC<{
  isLoaded: boolean;
  label: string;
  roleName: "agent_cli" | "webapi";
  token: Token | undefined;
  setToken: React.Dispatch<React.SetStateAction<Token | undefined>>;
}> = ({ isLoaded, label, token, setToken, roleName }) => {
  const allRoles = token?.roles;
  const isChecked = (allRoles && allRoles[roleName]) || false;

  const onChange = () =>
    setToken((token) => {
      if (token === undefined || token.roles === undefined) return;
      const currRoles = token.roles;
      const roles = {
        ...currRoles,
        [roleName]: !currRoles[roleName],
      };
      const newToken = { ...token, roles: roles };
      return newToken;
    });

  return isLoaded ? (
    <Checkbox
      checked={isChecked}
      indeterminate={false}
      label={label}
      onChange={onChange}
      inline={true}
    />
  ) : (
    <Checkbox
      indeterminate={true}
      disabled={true}
      label={label}
      inline={true}
    />
  );
};

export const ManageTokenDialog: React.FC<Props> = ({
  onFinish,
  existingToken,
  orgId,
}) => {
  const [newlyGeneratedToken, setNewlyGeneratedToken] =
    useState<TokenResponse | null>(null);

  const [currentToken, setCurrentToken] = useState<Token>();

  // Required to block out non-team-tier permissions:
  const { org } = useContext(OrgContext);
  const hasTeamsTier = Feature.hasTeamsTier({ org });

  useEffect(() => {
    let isMounted = true;
    if (orgId == null) return;
    if (existingToken) {
      setCurrentToken(existingToken);
      return;
    }
    createToken(orgId)
      .then((response) => {
        if (!isMounted) return;
        setNewlyGeneratedToken(response);
        setCurrentToken({ name: "", id: response.id, roles: response.roles });
      })
      .catch((err) => {
        AppToaster.show({
          message: `Failed to generate token: ${err}`,
          intent: "danger",
        });
      });

    // Abort any callbacks/promises on component unmounting
    return () => {
      isMounted = false;
    };
  }, [existingToken, orgId]);

  const removeToken = (token: Token) => {
    withToaster(
      () => deleteToken(orgId, token.id).then(() => onFinish()),
      (err) => `Failed to delete token: ${err.body?.error}`
    )();
  };

  const editToken = (token?: Token) => {
    if (!token || !token.id) return;
    withToaster(
      () => updateToken(orgId, token).then(() => onFinish()),
      (err) => `Failed to update token: ${err.body?.error}`
    )();
  };

  const isTokenLoaded =
    existingToken !== undefined || newlyGeneratedToken !== null;

  const webApiCheckbox = (
    <RoleCheckbox
      label="WebAPI"
      roleName="webapi"
      isLoaded={hasTeamsTier && isTokenLoaded}
      token={currentToken}
      setToken={setCurrentToken}
    />
  );

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (currentToken) {
      setCurrentToken({ ...currentToken, name: value });
    }
  };

  return (
    <DialogContainer>
      <h2>{existingToken ? "Update" : "Create"} a token</h2>
      <NewTokenContainer className={"dialogBody"} style={{ margin: "20px 0" }}>
        {!existingToken && (
          <p
            style={{
              backgroundColor: "rgb(255, 200, 200)",
              borderRadius: "5px",
              padding: "5px",
            }}
          >
            <strong>
              Copy this token now - you won't be able to view it again!
            </strong>
          </p>
        )}
        <NewTokenRow data-testid="secrets-name">
          <h4> Secrets Name </h4>
          <Path shareLink={"SEMGREP_APP_TOKEN"}></Path>
        </NewTokenRow>
        {/* Protect sensitive data by masking in fullstory recordings
            https://help.fullstory.com/hc/en-us/articles/360020623574 */}
        <form
          className="fs-mask dd-privacy-mask"
          data-testid="secrets-value-form"
        >
          <h4> Secrets Value </h4>
          {existingToken ? (
            <InputGroup disabled={true} value={"*****"} />
          ) : !newlyGeneratedToken ? (
            <InputGroup disabled={true} value={"loading..."} />
          ) : (
            <ShareLink focus={false} shareLink={newlyGeneratedToken.token} />
          )}
        </form>
        <NewTokenRow>
          <h4> Token Scopes</h4>
          {/* TODO Possible improvements: better typing, make the
              options dynamic. For now, it works well as is. */}
          <RoleCheckbox
            label="Agent (CI)"
            roleName="agent_cli"
            isLoaded={isTokenLoaded}
            token={currentToken}
            setToken={setCurrentToken}
          />
          {hasTeamsTier ? (
            webApiCheckbox
          ) : (
            <HoverCard withArrow shadow="sm" width={320}>
              <HoverCard.Dropdown p="md">
                <TeamsTierUpsell />
              </HoverCard.Dropdown>
              <HoverCard.Target>
                <span>{webApiCheckbox}</span>
              </HoverCard.Target>
            </HoverCard>
          )}
          {}
        </NewTokenRow>
        <NewTokenRow>
          <h4> Description (optional) </h4>
          <FlexDiv data-testid="token-description">
            <InputGroup
              placeholder="What is this token for?"
              asyncControl={true}
              value={currentToken?.name}
              onChange={handleDescriptionChange}
              style={{ width: "520px" }}
            />
            <div>
              <Button
                onClick={() => editToken(currentToken)}
                intent="primary"
                data-testid="save-token"
              >
                Update
              </Button>
              {existingToken && (
                <Button
                  style={{ marginLeft: "10px" }}
                  onClick={() => removeToken(existingToken)}
                  intent="danger"
                  data-testid="delete-token"
                >
                  Delete
                </Button>
              )}
            </div>
          </FlexDiv>
        </NewTokenRow>
      </NewTokenContainer>
    </DialogContainer>
  );
};
